import { getSDK } from '../../functions/sdk.js'
import { RAW_URL as SUPABASE_URL, SUPABASE_KEY } from '../../functions/supabaseClient.js'

const _dangerBtn = (label, color, onConfirm) => ({
  tag: 'button',
  padding: 'Z A',
  round: 'Z',
  borderWidth: '1px',
  borderColor: color,
  borderStyle: 'solid',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '600',
  background: (el, s) => s._confirming ? color : 'transparent',
  color: (el, s) => s._confirming ? 'white' : color,
  text: (el, s) => s._busy ? 'Working...' : s._confirming ? 'Confirm?' : s._done || label,
  ':hover': { opacity: '.85' },
  onClick: async (e, el, s) => {
    if (s._busy) return
    if (!s._confirming) {
      el.state.update({ _confirming: true })
      setTimeout(() => el.state.update({ _confirming: false }), 4000)
      return
    }
    el.state.update({ _confirming: false, _busy: true })
    try {
      await onConfirm(s)
      el.state.update({ _busy: false, _done: 'Done' })
      setTimeout(() => el.state.update({ _done: null }), 2000)
    } catch (err) {
      console.error(err)
      el.state.update({ _busy: false, _done: 'Failed' })
      setTimeout(() => el.state.update({ _done: null }), 2000)
    }
  }
})

const _dangerRow = (label, hint, btnLabel, color, onConfirm) => ({
  flow: 'x',
  align: 'center space-between',
  padding: 'A 0',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  Left: {
    flow: 'y',
    gap: 'Y',
    flex: '1',
    Label: { tag: 'span', fontSize: 'A', fontWeight: '500', color: 'title', text: label },
    Hint: { tag: 'span', fontSize: 'Y2', color: 'caption', text: hint }
  },
  Btn: _dangerBtn(btnLabel, color, onConfirm)
})

export const adminDanger = {
  extends: 'AppPage',
  metadata: { title: 'Danger zone / Admin / Symbols Workspace' },
  onInit: (el, s) => {
    if (s.root?.currentUserId && !el.call('isSuperAdmin')) {
      let root = el; while (root && !root.routes) root = root.parent
      if (root) el.router('/admin', root, {}, { pushState: true })
    }
  },

  AdminSidebar: {},

  DangerMain: {
    extends: 'AppMain',
    padding: '0',
    Header: { extends: 'PageHeader', LeftGroup: { Title: { text: 'Danger Zone' } } },

    Body: {
      overflow: 'auto', padding: 'B', flow: 'y', gap: 'B',

      DangerCard: {
        flow: 'y', gap: 'A', padding: 'B', round: 'Z2',
        background: 'overlay', borderWidth: '1px', borderStyle: 'solid', borderColor: 'red',

        DzTitle: { tag: 'h2', fontSize: 'B1', fontWeight: '800', color: 'red', margin: '0', text: 'Destructive Actions' },
        DzHint: { tag: 'p', fontSize: 'Z2', color: 'caption', margin: '0', text: 'These actions are irreversible. Superadmin only.' },

        DzActions: {
          flow: 'y', gap: 'Z', marginTop: 'A',

          DzExport: _dangerRow(
            'Export all user data',
            'Download all users, roles, and equity data as JSON',
            'Export JSON',
            'title',
            async (s) => {
              // migrated 2026-04-27 to SDK route workspaceProject.people/list
              const result = await getSDK().execute('workspaceProject.people', 'list')
              const data = Array.isArray(result) ? result : (result?.data || result?.people || result?.rows || [])
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
              const a = document.createElement('a')
              a.href = URL.createObjectURL(blob)
              a.download = `symbols-users-${new Date().toISOString().slice(0, 10)}.json`
              a.click()
              URL.revokeObjectURL(a.href)
            }
          ),

          DzDeactivateAll: _dangerRow(
            'Deactivate all non-core members',
            'Set status to "left" for everyone without a core role',
            'Deactivate',
            'red',
            async (s) => {
              // CU-10 §90: roles now in root.roles.byUser (not p.roles)
              const people = s.root?.people || []
              const byUser = s.root?.roles?.byUser || {}
              const coreRoles = new Set(['superadmin', 'admin', 'manager', 'member'])
              for (const p of people) {
                const legacyRoles = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : null
                const isCore = legacyRoles
                  ? legacyRoles.some(r => coreRoles.has(r))
                  : coreRoles.has(byUser[p.userId] || 'member')
                if (!isCore && p.status !== 'left') {
                  // migrated 2026-04-27 to SDK route workspaceProject.userProfiles/update
                  await getSDK().execute('workspaceProject.userProfiles', 'update', {
                    id: p.userId,
                    payload: { status: 'left' }
                  })
                }
              }
            }
          ),

          // CU-10 §90: DzResetRoles removed — user_roles table is dropped.
          // Role resets now require going through Mongo OrgMember.role
          // (server endpoint: PATCH /auth/org/:orgId/member-roles).
          // This danger action is disabled to prevent silent no-ops.



          DzPurgeChat: _dangerRow(
            'Purge all chat messages',
            'Delete every message across all channels — channel structure is preserved',
            'Purge messages',
            'red',
            // TODO[sdk-bulk-delete]: no SDK route for bulk-delete on
            // chat_messages — the per-id `chat.removeMessage` doesn't fit
            // the "purge everything" use case. This admin danger-zone
            // action requires a service-role key; once the workspace
            // wrapper exposes a /chat/purge admin route guarded by org
            // owner permissions, replace the raw fetch with
            // `getSDK().execute('workspaceProject.chat.messages', 'purge', {})`.
            async () => {
              await fetch(`${SUPABASE_URL}/rest/v1/chat_messages?id=not.is.null`, {
                method: 'DELETE',
                headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, Prefer: 'return=minimal' }
              })
            }
          )
        }
      }
    }
  }
}
